import {
  ArrowCornerUpRightIcon,
  ArrowRightCircleIcon,
  ColorBadge,
  Column,
  DataTableNaked,
  DiscountCircle,
  DollarCircleIcon,
  GraphIcon,
  InformationIconSmall,
  ListBullets1Icon,
  Loading,
  MiniTooltip,
  Search,
  SpoilerAlertAppleIcon,
  Theme,
  TruckSimpleFilledIcon,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { event } from 'react-fullstory';
import { createUseStyles } from 'react-jss';
import CustomerParticipationRateChart from '../../components/charts/customer-participation-rate-chart';
import { customerParticipationStatus, offerlistingStatuses } from '../../enums';
import routePaths from '../../route-paths';
import ComingSoonIcon from './icons/coming-soon';

const useStyles = createUseStyles({
  headerText: {
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '26px',
    letterSpacing: '-0.198px',
    margin: 0,
  },
  landingPageContainer: {
    border: `1px solid ${Theme.borderColor}`,
    borderRadius: '6px',
    padding: '24px',
    paddingBottom: '0px',
    marginBottom: '24px',
    minWidth: '694px',
    '& table': {
      boxShadow: 'none',
      '& td': {
        '&:first-child': {
          paddingLeft: '16px',
        },
      },
      '& th': {
        '&:first-child': {
          paddingLeft: '0',
        },
      },
    },
  },
  expandedRow: {
    extend: 'landingPageContainer',
    '& tr': {
      verticalAlign: 'top',
    },
  },
  header: {
    display: 'flex',
    position: 'relative',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '16px',
    '& h1': {
      extend: 'headerText',
    },
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  showDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '-0.084px',
    color: Theme.teal,
    cursor: 'pointer',
    marginLeft: '24px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    height: '36px',
  },
  searchBar: {
    width: '350px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
  },
  customerParticipation: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '123px',
  },
  chartContainer: {
    width: '35px',
    height: '35px',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '16px',
  },
  loadingContainer: {
    paddingBottom: '24px',
  },
  subText: {
    maxWidth: '500px',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: '24px',
  },
  columnHeader: {
    textTransform: 'uppercase',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: Theme.greyDark,
    letterSpacing: '-0.084px',
    width: '25%',
  },
  firstColumn: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    color: Theme.textColorPrimary,
    letterSpacing: '-0.176px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    gap: '4px',
    justifyContent: 'flex-start',
    width: '100%',
  },
  metricsColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'start',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '-0.084px',
    color: Theme.textColorPrimary,
  },
  table_container: {
    boxShadow: 'none',
    backgroundColor: Theme.white,
  },
  buyerSiteNameWrapper: {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    width: '100%',
  },
  buyerSiteName: {
    maxWidth: 'calc(100% - 24px)',
  },
  apple: {
    width: '16px',
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '16px',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '-0.084px',
  },
  tooltipText: {
    width: 159,
    fontWeight: 'normal',
    textAlign: 'left',
    textWrap: 'wrap',
    lineHeight: 'normal',
  },
  reminderContainer: {
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    lineHeight: '15px',
  },
  infoIcon: {
    height: 16,
    width: 16,
    fill: Theme.grey80,
    marginRight: '8px',
  },
  reminderText: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 400,
    color: Theme.grey80,
  },
  reminderStatus: {
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '-0.084px',
    color: Theme.greyDark,
    paddingLeft: '24px',
  },
});

export const CustomerSummary = ({ user, customerParticipationMetrics, loading, history }) => {
  const classes = useStyles();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  const customerInfo = customerParticipationMetrics?.customerParticipationSummaryQuery || [];
  const offersReceived = customerInfo?.filter((ci) => ci.offerStatus === customerParticipationStatus.OFFERS_RECEIVED).length || 0;
  const customerParticipationRate = customerInfo?.length ? Math.round((offersReceived / customerInfo.length) * 100) : 0;

  const handleOfferStatusClick = (e, row) => {
    e.stopPropagation();
    if (row.offerStatus === customerParticipationStatus.OFFERS_RECEIVED) {
      return history.push({
        pathname: `${routePaths.trucklane}`,
        state: {
          buyerSiteName: row.buyerSiteName,
          buyerSiteId: row.buyerSiteId,
          offerStatus: offerlistingStatuses.ACTIVE,
        },
      });
    }
  };

  const handleNegotiationClick = (e, row) => {
    e.stopPropagation();
    if (row.negotiationStatus === customerParticipationStatus.SENT) {
      return history.push(`${routePaths.negotiations}/${row._id}/${row.buyerSiteName}`);
    }
    if (row.negotiationStatus === customerParticipationStatus.RECEIVED) {
      return history.push({
        pathname: `${routePaths.trucklane}`,
        state: {
          buyerSiteName: row.buyerSiteName,
          buyerSiteId: row.buyerSiteId,
          offerStatus: offerlistingStatuses.NEGOTIATED,
        },
      });
    }
  };

  const handleAwardClick = (e, row) => {
    e.stopPropagation();
    if (row.awardStatus === customerParticipationStatus.AWARDED || row.awardStatus === customerParticipationStatus.AWARDED_ACCEPTED) {
      return history.push(`${routePaths.offerReview}/${row._id}/${row.buyerSiteName}`);
    }
  };

  const getOfferStatus = (row) => {
    if (row.offerStatus === customerParticipationStatus.OFFERS_RECEIVED) {
      return (
        <ColorBadge theme="green" Icon={ArrowCornerUpRightIcon} onClick={(e) => handleOfferStatusClick(e, row)}>
          Received
        </ColorBadge>
      );
    }
    if (row.offerStatus === customerParticipationStatus.NO_OFFERS) {
      return <ColorBadge theme="grey">No Offers</ColorBadge>;
    }
  };

  const getDisplayPercentage = (percentage) => {
    if (percentage < 1 && percentage > 0) {
      return percentage.toFixed(2);
    }
    return percentage;
  };

  const getOfferStatusRow = (row) => {
    return (
      <div className={classes.metricsColumn}>
        {getOfferStatus(row)}
        {selectedRows.includes(row._id) && (
          <div className={classes.detailsContainer}>
            <span>
              <TruckSimpleFilledIcon /> Truck lanes: {row.trucklanesWithOffers}
            </span>
            {!row.hideCostMetrics && (
              <span>
                <DiscountCircle /> Offer cost recovery: {row.offerCostRecovery}%
              </span>
            )}
            <span>
              <GraphIcon /> Inventory with offers: {getDisplayPercentage(row.inventoryWithOffersPercentage)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const getNegotiationStatus = (row) => {
    if (row.negotiationStatus === customerParticipationStatus.SENT) {
      return (
        <ColorBadge theme="teal" Icon={ArrowCornerUpRightIcon} onClick={(e) => handleNegotiationClick(e, row)}>
          Sent
        </ColorBadge>
      );
    }
    if (row.negotiationStatus === customerParticipationStatus.RECEIVED) {
      return (
        <ColorBadge theme="green" Icon={ArrowCornerUpRightIcon} onClick={(e) => handleNegotiationClick(e, row)}>
          Received
        </ColorBadge>
      );
    }
    if (row.negotiationStatus === customerParticipationStatus.NONE_SENT) {
      return <ColorBadge theme="grey">None Sent</ColorBadge>;
    }
  };

  const getNegotiationStatusRow = (row) => {
    return (
      <div className={classes.metricsColumn}>
        {getNegotiationStatus(row)}
        {selectedRows.includes(row._id) && (
          <div className={classes.detailsContainer}>
            <span>
              <DollarCircleIcon /> Bid increases: ${accounting.formatNumber(row.bidIncrease, 2)}
            </span>
            <span>
              <ArrowRightCircleIcon /> Sent negotiations: {row.sentNegotiationsCount}
            </span>
            <span>
              <ArrowRightCircleIcon style={{ transform: 'rotate(180deg)' }} /> Returned negotiations: {row.receivedNegotiationsCount}
            </span>
          </div>
        )}
      </div>
    );
  };

  const getAwardStatus = (row) => {
    if (row.awardStatus === customerParticipationStatus.AWARDED) {
      return {
        badge: (
          <ColorBadge theme="green" Icon={ArrowCornerUpRightIcon} onClick={(e) => handleAwardClick(e, row)}>
            Awarded
          </ColorBadge>
        ),
        metricName: 'Awarded',
      };
    }
    if (row.awardStatus === customerParticipationStatus.NONE_AWARDED) {
      return { badge: <ColorBadge theme="grey">None Awarded</ColorBadge>, metricName: 'Awarded' };
    }
    if (row.awardStatus === customerParticipationStatus.AWARDED_ACCEPTED) {
      return {
        badge: (
          <ColorBadge theme="green" Icon={ArrowCornerUpRightIcon} onClick={(e) => handleAwardClick(e, row)}>
            {row.awardStatus}
          </ColorBadge>
        ),
        metricName: 'Awarded + Accepted',
      };
    }
    if (row.awardStatus === customerParticipationStatus.ACCEPTED) {
      return { badge: <ColorBadge theme="green">{row.awardStatus}</ColorBadge>, metricName: 'Accepted' };
    }
    return { badge: <ColorBadge theme="grey">None Awarded</ColorBadge>, metricName: 'Awarded' };
  };

  const getAwardStatusRow = (row) => {
    const { badge, metricName } = getAwardStatus(row);
    return (
      <div className={classes.metricsColumn}>
        {badge}
        {selectedRows.includes(row._id) && (
          <div className={classes.detailsContainer}>
            <span>
              <DollarCircleIcon /> {`${metricName}: $${accounting.formatNumber(row.totalAwardedRevenue, 2)}`}
            </span>
            <span>
              <TruckSimpleFilledIcon /> Truck lanes: {row.trucklanesWithAwards}
            </span>
            {!row.hideCostMetrics && (
              <span>
                <DiscountCircle /> Cost recovery: {row.awardCostRecovery}%
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const tooltipText = (status, timestamp) => {
    if (status === 'Not Sent') {
      return 'A reminder email was not sent because the customer submitted offers.';
    }
    const date = moment(timestamp).format('MMMM DD');
    const time = moment(timestamp).format('hh:mm A');
    const timezone = new Date(timestamp).toLocaleString('en-US', { timeZoneName: 'short' }).split(' ')[3];
    if (status === 'Scheduled') {
      return (
        <div>
          Scheduled for:
          <br /> {date} at {time} {timezone}
        </div>
      );
    }
    if (status === 'Sent') {
      return (
        <div>
          Reminder email sent:
          <br /> {date} at {time} {timezone}
        </div>
      );
    }
    return 'A reminder email has not been scheduled.';
  };

  const getCustomer = (row) => {
    return (
      <span className={classes.firstColumn}>
        <span className={classes.buyerSiteNameWrapper}>
          <span className={classes.buyerSiteName}>{row.buyerSiteName}</span>
          {row.isVendorOfRecord && <SpoilerAlertAppleIcon className={classes.apple} />}
        </span>
        {selectedRows.includes(row._id) && (
          <MiniTooltip
            text={
              <div className={classes.tooltipText}>
                <span>{tooltipText(row.reminderStatus, row.reminderTimestamp)}</span>
              </div>
            }
          >
            <div className={classes.reminderContainer}>
              <div className={classes.reminderText}>
                <InformationIconSmall className={classes.infoIcon} /> REMINDER
              </div>
              <div className={classes.reminderStatus}>{row.reminderStatus}</div>
            </div>
          </MiniTooltip>
        )}
      </span>
    );
  };

  const customerParticipation = (
    <div className={classes.headerRight}>
      <Search placeholder="Search by customer name" className={classes.searchBar} onChange={(e) => setSearchTerm(e)} />
      {!loading && (
        <div className={classes.customerParticipation}>
          <div className={classes.chartContainer}>
            <CustomerParticipationRateChart
              cutout="50%"
              subjectColor={Theme.green}
              backgroundColor={Theme.green30}
              participationRate={customerParticipationRate}
            />
          </div>
          <div>{customerParticipationRate}% Participation</div>
        </div>
      )}
    </div>
  );

  const columns = [
    new Column({
      field: 'buyerSiteName',
      displayName: 'Customer',
      formatter: (_, row) => getCustomer(row),
      className: classes.columnHeader,
    }),
    new Column({
      field: 'offerStatus',
      displayName: 'Offers',
      defaultSort: true,
      sortField: (row) => {
        const statusPriority = row.offerStatus === 'OFFERS RECEIVED' ? 0 : 1;
        // Secondary sort by buyerSiteName
        const siteName = row.buyerSiteName || '';
        // Combine both values - status priority as primary, site name as secondary
        return `${statusPriority}_${siteName}`;
      },
      formatter: (_, row) => getOfferStatusRow(row),
      className: classes.columnHeader,
    }),
    new Column({
      field: 'negotiationStatus',
      displayName: 'Negotiations',
      formatter: (_, row) => getNegotiationStatusRow(row),
      className: classes.columnHeader,
    }),
    new Column({
      field: 'awardStatus',
      displayName: 'Awards',
      formatter: (_, row) => getAwardStatusRow(row),
      className: classes.columnHeader,
    }),
  ];

  const handleShowDetailsClick = () => {
    event('Show All Details clicked', {
      timestamp: new Date().toISOString(),
      email: user?.email,
      transactionCycleId: user?.site?.transactionCycleId,
    });
    setSelectedRows(customerInfo.map((ci) => ci._id));
  };

  const handleHideDetailsClick = () => {
    setSelectedRows([]);
  };

  const handleRowClick = (row) => {
    if (selectedRows.includes(row._id)) {
      setSelectedRows(selectedRows.filter((id) => id !== row._id));
    } else {
      event(`Customer ${row.buyerSiteName} expanded`, {
        timestamp: new Date().toISOString(),
        email: user?.email,
        transactionCycleId: user?.site?.transactionCycleId,
      });
      setSelectedRows([...selectedRows, row._id]);
    }
  };

  const table = () => {
    if (loading)
      return (
        <div className={classes.loadingContainer}>
          <Loading loading={loading} size="small" />
        </div>
      );
    if (customerInfo.length > 0)
      return (
        <DataTableNaked
          columns={columns}
          data={customerInfo}
          searchText={searchTerm}
          highlightRowId={''}
          noDataMessage={'No customers match your search'}
          onRowClick={handleRowClick}
        />
      );
    return (
      <div className={classes.textContainer}>
        <ComingSoonIcon />
        <span className={classes.subText}>
          Once you list to your customers, they will show up here along with metrics to help you understand your cycle outcomes.
        </span>
      </div>
    );
  };

  return (
    <div className={selectedRows.length > 0 ? classes.expandedRow : classes.landingPageContainer}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <h1>Customer Summary</h1>
          {customerInfo.length > 0 && selectedRows.length === customerInfo.length && (
            <span className={classes.showDetails} onClick={handleHideDetailsClick}>
              <ListBullets1Icon />
              {'Hide All Details'}
            </span>
          )}
          {customerInfo.length > 0 && selectedRows.length < customerInfo.length && (
            <span className={classes.showDetails} onClick={handleShowDetailsClick}>
              <ListBullets1Icon />
              {'Show All Details'}
            </span>
          )}
        </div>
        {customerParticipation}
      </div>
      <div className={classes.contentContainer}>{table()}</div>
    </div>
  );
};

CustomerSummary.propTypes = {
  user: PropTypes.object,
  customerParticipationMetrics: PropTypes.object,
  loading: PropTypes.bool,
  history: PropTypes.object,
};
